import React, { useEffect, useState } from 'react';
import { X, Calendar, Target, Flame, FileText } from 'lucide-react';
import { format } from 'date-fns';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from '@/components/ui/drawer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { WellnessUserChallenge, WellnessChallengeTemplate } from '@/types/wellnessChallenges';
import ChallengeCorrelationsPanel from './ChallengeCorrelationsPanel';

interface ChallengeWithTemplate extends WellnessUserChallenge {
  template?: WellnessChallengeTemplate | null;
}

interface ChallengeDetailDrawerProps {
  challenge: ChallengeWithTemplate | null;
  isOpen: boolean;
  onClose: () => void;
}

interface CheckinNote {
  id: string;
  checkin_date: string;
  completed: boolean;
  notes: string | null;
}

const CATEGORY_COLORS: Record<string, string> = {
  sleep: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
  screen_time: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  nutrition: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  movement: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
  meditation: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  substance: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  stress: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  custom: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
};

export function ChallengeDetailDrawer({ challenge, isOpen, onClose }: ChallengeDetailDrawerProps) {
  const [checkins, setCheckins] = useState<CheckinNote[]>([]);
  const [streak, setStreak] = useState(0);
  const [completedDays, setCompletedDays] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!challenge || !isOpen) return;

    const fetchCheckins = async () => {
      setLoading(true);
      try {
        // Fetch checkins for this challenge
        const { data, error } = await supabase
          .from('wellness_challenge_checkins')
          .select('id, checkin_date, completed, notes')
          .eq('user_challenge_id', challenge.id)
          .order('checkin_date', { ascending: false })
          .limit(20);

        if (error) throw error;

        const allCheckins = data || [];
        
        // Get recent notes (limit to 5 with actual notes)
        const withNotes = allCheckins.filter(c => c.notes).slice(0, 5);
        setCheckins(withNotes);

        // Calculate completed days
        const completed = allCheckins.filter(c => c.completed).length;
        setCompletedDays(completed);

        // Calculate streak (consecutive completed days from most recent)
        let currentStreak = 0;
        const sortedByDate = [...allCheckins].sort(
          (a, b) => new Date(b.checkin_date).getTime() - new Date(a.checkin_date).getTime()
        );
        
        for (const checkin of sortedByDate) {
          if (checkin.completed) {
            currentStreak++;
          } else {
            break;
          }
        }
        setStreak(currentStreak);
      } catch (err) {
        console.error('Error fetching challenge checkins:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCheckins();
  }, [challenge, isOpen]);

  if (!challenge) return null;

  const title = challenge.custom_title || challenge.template?.title || 'Challenge';
  const description = challenge.template?.description;
  const totalDays = challenge.duration_days;
  const progressPercent = totalDays > 0 ? Math.round((completedDays / totalDays) * 100) : 0;
  const categoryColor = CATEGORY_COLORS[challenge.category] || CATEGORY_COLORS.custom;

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="h-[85vh] max-h-[85vh]">
        <DrawerHeader className="border-b border-border/50 pb-4">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <DrawerTitle className="text-xl font-semibold text-foreground">
                {title}
              </DrawerTitle>
              <Badge 
                variant="outline" 
                className={`text-xs capitalize ${categoryColor}`}
              >
                {challenge.category.replace('_', ' ')}
              </Badge>
            </div>
            <DrawerClose asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>

        <ScrollArea className="flex-1 px-4 py-4">
          <div className="space-y-6">
            {/* Progress Section */}
            <section className="space-y-3">
              <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                Progress
              </h3>
              <div className="bg-secondary/30 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {completedDays} of {totalDays} days complete
                  </span>
                  <span className="font-medium text-foreground">{progressPercent}%</span>
                </div>
                <Progress value={progressPercent} className="h-2" />
                
                {streak > 0 && (
                  <div className="flex items-center gap-2 pt-1">
                    <Flame className="h-4 w-4 text-orange-400" />
                    <span className="text-sm text-muted-foreground">
                      {streak} day streak
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                  <Calendar className="h-3 w-3" />
                  <span>
                    {format(new Date(challenge.start_date), 'MMM d')} - {format(new Date(challenge.end_date), 'MMM d, yyyy')}
                  </span>
                </div>
              </div>
            </section>

            {/* Why This Matters Section */}
            {(challenge.reason_text || description) && (
              <section className="space-y-3">
                <h3 className="text-sm font-medium text-foreground">Why this matters</h3>
                <div className="bg-secondary/30 rounded-lg p-4 space-y-2">
                  {challenge.reason_text && (
                    <p className="text-sm text-foreground leading-relaxed">
                      {challenge.reason_text}
                    </p>
                  )}
                  {description && (
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {description}
                    </p>
                  )}
                </div>
              </section>
            )}

            {/* Correlations Section */}
            <section className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">What your data suggests so far…</h3>
              <div className="bg-secondary/30 rounded-lg p-4">
                <ChallengeCorrelationsPanel
                  userId={challenge.user_id}
                  category={challenge.category}
                  metricSource={challenge.metric_source}
                  metricKey={challenge.metric_key}
                />
              </div>
            </section>

            {/* Notes Section */}
            {checkins.length > 0 && (
              <section className="space-y-3">
                <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  Recent Notes
                </h3>
                <div className="space-y-2">
                  {checkins.map((checkin) => (
                    <div
                      key={checkin.id}
                      className="bg-secondary/30 rounded-lg p-3 space-y-1"
                    >
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(checkin.checkin_date), 'MMM d, yyyy')}
                      </p>
                      <p className="text-sm text-foreground">{checkin.notes}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Goal if present */}
            {challenge.custom_goal && (
              <section className="space-y-3">
                <h3 className="text-sm font-medium text-foreground">Your Goal</h3>
                <div className="bg-secondary/30 rounded-lg p-4">
                  <p className="text-sm text-foreground">{challenge.custom_goal}</p>
                </div>
              </section>
            )}
          </div>
        </ScrollArea>
      </DrawerContent>
    </Drawer>
  );
}

export default ChallengeDetailDrawer;
